import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonClickData } from '../../../../store/models/analytics/ButtonClicks';
import { TopicMap } from '../../../../store/slices/topicSlice';
import { colors } from '../../../../theme/palette';
import { ChartContent } from './ChartContent';

const useStyles = makeStyles({
	tableHeader: {
		backgroundColor: colors.grayDark,
		position: 'sticky',
		top: 0,
		zIndex: 1,
	},
	headerCell: {
		color: colors.pureWhite,
		borderBottom: `2px solid ${colors.grayDark20}`,
		padding: '8px 16px',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: colors.grayDark20,
		},
	},
	headerCellSortable: {
		color: colors.pureWhite,
		borderBottom: `2px solid ${colors.grayDark20}`,
		padding: '2px 16px',
		cursor: 'pointer',
		position: 'relative',
		'&:hover': {
			backgroundColor: colors.grayDark20,
		},
		'&::after': {
			content: '""',
			position: 'absolute',
			right: '10px',
			top: '50%',
			transform: 'translateY(-50%)',
			width: '0',
			height: '0',
			borderLeft: '4px solid transparent',
			borderRight: '4px solid transparent',
		},
	},
	headerCellSortedAsc: {
		'&::after': {
			borderBottom: '4px solid white',
		},
	},
	headerCellSortedDesc: {
		'&::after': {
			borderTop: '4px solid white',
		},
	},
	row: {
		'&:nth-of-type(odd)': {
			backgroundColor: colors.grayDark5,
		},
		'&:nth-of-type(even)': {
			backgroundColor: colors.pureWhite,
		},
		'&:hover': {
			backgroundColor: colors.grayLight20,
		},
	},
	cell: {
		color: colors.grayDark,
		padding: '5px 16px',
		borderBottom: `1px solid ${colors.grayDark20}`,
		maxWidth: '200px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	buttonNameCell: {
		color: colors.grayDark,
		padding: '2px 16px',
		borderBottom: `1px solid ${colors.grayDark20}`,
		maxWidth: '250px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	pageNameCell: {
		color: colors.grayDark,
		padding: '5px 16px',
		borderBottom: `1px solid ${colors.grayDark20}`,
		maxWidth: '200px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	totalCell: {
		color: colors.grayDark,
		borderRadius: 4,
		borderBottom: `1px solid ${colors.grayDark20}`,
		padding: '5px 16px',
		minWidth: '100px',
		width: '100px',
	},
});

interface ButtonClicksTableProps {
	buttonClickData: ButtonClickData[];
	topics: TopicMap;
}

const getTopicName = (topic: any) => {
	if (!topic) return '';
	const en = topic.name.find((n: any) => n.language === 'en');
	return en ? en.name : topic.name[0]?.name || '';
};

type SortDirection = 'asc' | 'desc' | null;
type SortField = 'buttonName' | 'totalClicks' | null;

export const ButtonClicksTable: React.FC<ButtonClicksTableProps> = ({ buttonClickData, topics }) => {
	const classes = useStyles();
	const [sortDirection, setSortDirection] = useState<SortDirection>(null);
	const [sortField, setSortField] = useState<SortField>(null);

	const handleSort = (field: SortField) => {
		if (sortField !== field) {
			setSortField(field);
			setSortDirection('asc');
		} else if (sortDirection === 'asc') {
			setSortDirection('desc');
		} else if (sortDirection === 'desc') {
			setSortField(null);
			setSortDirection(null);
		}
	};

	const sortedData = useMemo(() => {
		if (!sortDirection || !sortField) return buttonClickData;

		return [...buttonClickData].sort((a, b) => {
			if (sortField === 'buttonName') {
				const nameA = a.buttonName.toLowerCase();
				const nameB = b.buttonName.toLowerCase();

				if (sortDirection === 'asc') {
					return nameA.localeCompare(nameB);
				} else {
					return nameB.localeCompare(nameA);
				}
			} else if (sortField === 'totalClicks') {
				const totalA = a.clicks.reduce((sum, c) => sum + c.value, 0);
				const totalB = b.clicks.reduce((sum, c) => sum + c.value, 0);

				if (sortDirection === 'asc') {
					return totalA - totalB;
				} else {
					return totalB - totalA;
				}
			}
			return 0;
		});
	}, [buttonClickData, sortDirection, sortField]);

	const getHeaderCellClass = (field: SortField) => {
		if (sortField !== field) {
			return classes.headerCellSortable;
		}

		let className = classes.headerCellSortable;
		if (sortDirection === 'asc') {
			className += ` ${classes.headerCellSortedAsc}`;
		} else if (sortDirection === 'desc') {
			className += ` ${classes.headerCellSortedDesc}`;
		}
		return className;
	};

	return (
		<ChartContent
			name={'BUTTON/HYPERLINK CLICK STATS'}
			toolTipMsg={'The number of clicks on the button or hyperlink for each topic.'}
		>
			<TableContainer
				component={Paper}
				style={{
					width: '100%',
					height: '300px',
					borderRadius: 0,
					boxShadow: 'none',
					marginTop: '10px',
					paddingBottom: '30px',
					overflowX: 'hidden',
				}}
			>
				<Table style={{ tableLayout: 'fixed', width: '100%' }}>
					<TableHead>
						<TableRow className={classes.tableHeader}>
							<TableCell
								className={getHeaderCellClass('buttonName')}
								onClick={() => handleSort('buttonName')}
								style={{ width: '50%' }}
							>
								Button/Hyperlink Name
							</TableCell>
							<TableCell className={classes.headerCell} style={{ width: '35%' }}>
								Page Name
							</TableCell>
							<TableCell
								align="right"
								className={getHeaderCellClass('totalClicks')}
								onClick={() => handleSort('totalClicks')}
								style={{ width: '15%' }}
							>
								Total
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody style={{ overflow: 'auto' }}>
						{sortedData.map((button) => {
							const topicId = button.topicId;
							const topic = topics[topicId];
							const topicName = getTopicName(topic);
							const totalClicks = button.clicks.reduce((sum, c) => sum + c.value, 0);
							return (
								<TableRow key={button.buttonId} className={classes.row}>
									<TableCell className={classes.buttonNameCell} title={button.buttonName}>
										{button.buttonName}
									</TableCell>
									<TableCell className={classes.pageNameCell} title={topicName}>
										{topicName}
									</TableCell>
									<TableCell align="right" className={classes.totalCell}>
										{totalClicks}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</ChartContent>
	);
};
